import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Switch } from '@/components/ui';
import type {
  PaymentLinkIdempotencyMode,
  PaymentLinkServiceConfigRequest,
} from '@/types/payment-link';

interface ServiceConfigFormProps {
  mode: 'create';
  onSubmit: (body: PaymentLinkServiceConfigRequest) => void;
  isPending: boolean;
}

/**
 * Service-freischalten Formular (Spec 505 Inc 2b/3, Wizard Schritt 2). Entry-agnostic (props only).
 * `IdempotencyMode`: `new-per-click` is selectable, `reuse-open-transaction` is visible but
 * DISABLED with a "folgt"/"coming soon" hint (D-Idempotency — the v1 service rejects it with 400,
 * so the UI never offers it). The Click-Webhook is an optional toggle + URL; enabling it on submit
 * triggers the one-time ClickWebhookSecretCleartext reveal (handled by the caller).
 */
export function ServiceConfigForm({ onSubmit, isPending }: ServiceConfigFormProps) {
  const { t } = useTranslation('payment-link');
  const [idempotencyMode, setIdempotencyMode] = useState<PaymentLinkIdempotencyMode>('new-per-click');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');

  const handleSubmit = () => {
    const body: PaymentLinkServiceConfigRequest = {
      Enabled: true,
      IdempotencyMode: idempotencyMode,
    };
    if (webhookEnabled) {
      body.ClickNotification = { Enabled: true, Url: webhookUrl };
    }
    onSubmit(body);
  };

  const modes: PaymentLinkIdempotencyMode[] = ['new-per-click', 'reuse-open-transaction'];

  return (
    <div className="space-y-6">
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">{t('serviceConfig.idempotencyLabel')}</legend>
        {modes.map((mode) => {
          const disabled = mode === 'reuse-open-transaction';
          return (
            <label
              key={mode}
              className={`flex items-center gap-2 text-sm ${disabled ? 'text-muted-foreground' : ''}`}
            >
              <input
                type="radio"
                name="pl-idempotency"
                value={mode}
                checked={idempotencyMode === mode}
                disabled={disabled}
                onChange={() => setIdempotencyMode(mode)}
              />
              <span>{t(`serviceConfig.idempotency.${mode}`)}</span>
              {disabled && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {t('serviceConfig.idempotencyReuseHint')}
                </span>
              )}
            </label>
          );
        })}
      </fieldset>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="pl-webhook-toggle">{t('serviceConfig.clickWebhookToggle')}</Label>
          <Switch
            id="pl-webhook-toggle"
            checked={webhookEnabled}
            onCheckedChange={setWebhookEnabled}
            disabled={isPending}
          />
        </div>
        {webhookEnabled && (
          <div className="space-y-1">
            <Label htmlFor="pl-webhook-url">{t('serviceConfig.clickWebhookUrlLabel')}</Label>
            <Input
              id="pl-webhook-url"
              type="url"
              value={webhookUrl}
              placeholder={t('serviceConfig.clickWebhookUrlPlaceholder')}
              onChange={(e) => setWebhookUrl(e.target.value)}
              disabled={isPending}
            />
          </div>
        )}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={isPending || (webhookEnabled && !webhookUrl.trim())}
      >
        {t('serviceConfig.enable')}
      </Button>
    </div>
  );
}
